// work designflow — порт дизайнерского флоу pothos (генерация N + отгрузка)
// на work-api (спека .private/briefs/work-designer-flow-port.md, 2026-08-21).
//
// Две поверхности:
//   admin  (X-Admin-Secret)   — приёмка исходника, рендер мастера, отладка N;
//   плагин (X-Dev-Plugin-Token) — манифест, ассеты статус-бара, приём кадров,
//                                 финализация.
//
// Формы ЗАПРОСОВ плагина заданы самим плагином (figma_plugin/ui.html) и
// менять их нельзя — схемы описывают то, что он реально шлёт, а не то, что
// было бы удобнее.
import { z } from 'zod';

const Locale = z.string().trim().min(1).max(60);
const Merchant = z.string().trim().min(1).max(160);

// ─── admin ──────────────────────────────────────────────────────────────────

/** Модальность исходника (мигр. 0073): происхождение кадра, не формат мастера
 *  — размер всегда идёт от самого исходника. '' = легаси-строка до колонки. */
export const WorkSourceModalitySchema = z.enum(
  ['', 'screenshot_iphone', 'screenshot_android', 'screenshot_other', 'pdf']);
export type WorkSourceModality = z.infer<typeof WorkSourceModalitySchema>;

/** Тип площадки исходника: агрегатор у плагина явный флаг (списка мерчантов
 *  на заведении нет — его сеет AI-скан скриншота), '' = легаси/мерчант. */
export const WorkSourceTypeSchema = z.enum(['', 'merchant', 'aggregator']);
export type WorkSourceType = z.infer<typeof WorkSourceTypeSchema>;

export const WorkDesignIngestReqSchema = z.object({
  locale: Locale,
  /** Страна каталога — полным именем («United_Arab_Emirates»), как в индексе. */
  country: z.string().trim().max(80).default(''),
  merchant_name: Merchant,
  /** Имя мерчанта на языке локали — только для глаз команды. */
  name_local: z.string().trim().max(160).default(''),
  kind: z.string().trim().max(40).default('membership'),
  origin: z.string().trim().max(200).default(''),
  note: z.string().trim().max(500).default(''),
  /** Многомерчантный исходник (агрегатор): список мерчантов внутри одной
   *  карточки; N = сумма капов по их ячейкам. Пусто = одиночный мерчант. */
  merchants: z.array(z.string().trim().min(1).max(160)).max(200).default([]),
  /** Тип площадки (мигр. 0073); у dev/админ-путей остаётся '' — там тип
   *  по-прежнему выводится из merchants[]. */
  source_type: WorkSourceTypeSchema.default(''),
  modality: WorkSourceModalitySchema.default(''),
  /** Второй язык документа именем каталога ('English'); '' = нет. */
  lang2: z.string().trim().max(40).default(''),
  /** Повторный залив в СУЩЕСТВУЮЩУЮ агрегаторную ячейку заводит ВАРИАНТ
   *  исходника (мигр. 0074) — sub_merchant говорит, чей экран на скриншоте.
   *  Без as_variant повторный залив остаётся безопасным no-op (existing) —
   *  вариант создаётся только по явному намерению отправителя. */
  sub_merchant: z.string().trim().max(160).default(''),
  as_variant: z.boolean().default(false),
  /** Байты исходного скриншота, base64 (≈8 МБ сырых). */
  image_b64: z.string().min(1).max(12_000_000),
  content_type: z.string().trim().max(60).default('image/png'),
});
export type WorkDesignIngestReq = z.infer<typeof WorkDesignIngestReqSchema>;

// ─── Плагин: «Добавить исходник» + реестр локалей для формы ─────────────────
// Заведение из плагина шлёт ТОТ ЖЕ Req (auth — X-Dev-Plugin-Token); origin
// проставляет сервер. Реестр локалей — плановые 35 (contracts vocab/plan),
// плагин строит по нему селекты страна → язык; свой список ему держать негде.

export const WorkDesignLocaleSchema = z.object({
  code: z.string(),        // 'sv_SE'
  iso: z.string(),         // 'SE'
  country: z.string(),     // 'Sweden' (вокабуляр каталога, с подчёркиваниями)
  language: z.string(),    // 'Swedish'
});
export type WorkDesignLocale = z.infer<typeof WorkDesignLocaleSchema>;

export const WorkDesignLocalesResSchema = z.object({
  ok: z.boolean(),
  locales: z.array(WorkDesignLocaleSchema).default([]),
  /** Варианты второго языка документа (имена каталога + 'English'). */
  languages: z.array(z.string()).default([]),
});
export type WorkDesignLocalesRes = z.infer<typeof WorkDesignLocalesResSchema>;

// ─── Dev-кабинет: заведение и список исходников (2026-08-25) ────────────────
// Заведение из кабинета использует ТОТ ЖЕ Req, что админ-ингест (auth другой:
// Firebase + cap dev). Тип «агрегатор» = непустой merchants[].

/** Строка списка исходников в Dev-кабинете. */
export const WorkDevDesignSourceSchema = z.object({
  locale: z.string(),
  merchant_name: z.string(),
  kind: z.string(),
  /** pending_eval | ready | no_code | eval_failed */
  status: z.string(),
  eval_status: z.string().default(''),
  country: z.string().default(''),
  note: z.string().default(''),
  /** Агрегатор: сколько мерчантов внутри (0 = единичный). */
  merchants_count: z.number().int().default(0),
  created_at: z.string().default(''),
  /** Последняя версия мастера (0 = дизайнер ещё не сохранял). */
  master_version: z.number().int().default(0),
  /** Мастер зафиксирован «готово к размножению» — пайплайн данных запущен. */
  master_ready: z.boolean().default(false),
  /** Кадров ячейки, отклонённых QA и ждущих перерисовки. */
  rework: z.number().int().default(0),
});
export type WorkDevDesignSource = z.infer<typeof WorkDevDesignSourceSchema>;

export const WorkDevDesignSourcesResSchema = z.object({
  ok: z.boolean(),
  sources: z.array(WorkDevDesignSourceSchema).default([]),
});
export type WorkDevDesignSourcesRes = z.infer<typeof WorkDevDesignSourcesResSchema>;

export const WorkDesignIngestResSchema = z.object({
  ok: z.boolean(),
  existing: z.boolean(),
  status: z.string(),
  image_r2_key: z.string().default(''),
  /** Залив стал ВАРИАНТОМ существующей агрегаторной ячейки (мигр. 0074). */
  variant: z.boolean().default(false),
  variant_id: z.string().default(''),
});
export type WorkDesignIngestRes = z.infer<typeof WorkDesignIngestResSchema>;

/** Вариант исходника агрегаторной ячейки в списке задач: id '' = основной
 *  исходник самой ячейки. Сортировка — лучшее разрешение вперёд. */
export const WorkDesignVariantSchema = z.object({
  id: z.string(),
  sub_merchant: z.string().default(''),
  w: z.number().int().default(0),
  h: z.number().int().default(0),
});
export type WorkDesignVariant = z.infer<typeof WorkDesignVariantSchema>;

/** Обе части опциональны: пустой запрос = «возьми любую ячейку из очереди». */
export const WorkDesignRenderReqSchema = z.object({
  locale: Locale.optional().default(''),
  merchant_name: Merchant.optional().default(''),
});
export type WorkDesignRenderReq = z.infer<typeof WorkDesignRenderReqSchema>;

export const WorkDesignRenderResSchema = z.object({
  ok: z.boolean(),
  /** idle | pending_eval | eval_failed | no_code | ready */
  status: z.string(),
  reason: z.string().optional(),
  locale: z.string().optional(),
  merchant: z.string().optional(),
});
export type WorkDesignRenderRes = z.infer<typeof WorkDesignRenderResSchema>;

export const WorkDesignSlotsQuerySchema = z.object({
  locale: Locale,
  merchant: Merchant,
});
export type WorkDesignSlotsQuery = z.infer<typeof WorkDesignSlotsQuerySchema>;

export const WorkDesignSlotsResSchema = z.object({
  ok: z.boolean(),
  accepted: z.number(),
  delivered: z.number(),
  cap: z.number(),
  n: z.number(),
});
export type WorkDesignSlotsRes = z.infer<typeof WorkDesignSlotsResSchema>;


// ─── плагин: манифест ───────────────────────────────────────────────────────
// Форма 1:1 с pothos `getMyDesignerAssignmentExport` — плагин её уже разбирает
// (`manifest_loaded`). Новые поля (n, cap_used, version) добавлены СБОКУ, у
// корня ответа, чтобы старый разбор манифеста не поехал.

// ── Стиль кодов: override дизайнера ────────────────────────────────────────
// Дизайнер задаёт стиль кодов КОНКРЕТНОГО мерчанта правкой блока
// MERCHANT_CODE_STYLE в своём figma_plugin/ui.html; плагин шлёт объект
// целиком в `?style=<url-encoded JSON>` designManifest. Здесь перечислено
// РОВНО то, что воркер умеет донести до рендера (pothos-encode: qr.py,
// aztec.py, pdf417.py, datamatrix.py, barcode_render.py — все читают
// module_color/background_color, qr.py ещё module_shape/error_correction).
//
// Нормализация — В СХЕМЕ (contracts.md): регистр shape/EC приводится тут, а
// не молчаливым `||` в хендлере. Незнакомые ключи (клиентский `code_recolor`,
// пункт 13 багрепорта дизайнера) НЕ ошибка — но и не молчание: ответ несёт их
// списком в `code_style.ignored`.
//
// Цвет — РОВНО `#RGB` или `#RRGGBB`, не «похоже на hex». Ширину диктует
// рендер: `hex_to_rgb` (functions/pothos-encode/utils.py) разворачивает 3
// символа и читает 6. Всё прочее он берёт МОЛЧА и неправильно (проверено
// вызовом): 4 символа роняют его в собственный default (чёрный у модулей,
// белый у фона — НЕ донорский цвет), 5 теряют полубайт («#ABCDE» → ABCD0E),
// 7 и 8 читают первые 6 и отбрасывают хвост вместе с альфой. Принять такое
// значило бы вернуть ровно тот баг, от которого этот параметр чинят: 200 ok
// и цвет, которого дизайнер не просил.
const StyleColor = z.string().trim().regex(/^#(?:[0-9a-fA-F]{3}|[0-9a-fA-F]{6})$/);

export const WorkCodeStyleShapeSchema = z.enum(['square', 'rounded', 'round', 'dot', 'circle']);
export type WorkCodeStyleShape = z.infer<typeof WorkCodeStyleShapeSchema>;

export const WorkCodeStyleEcSchema = z.enum(['L', 'M', 'Q', 'H']);
export type WorkCodeStyleEc = z.infer<typeof WorkCodeStyleEcSchema>;

export const WorkCodeStyleOverrideSchema = z.object({
  module_shape: z.string().transform(s => s.trim().toLowerCase())
    .pipe(WorkCodeStyleShapeSchema).optional(),
  module_color: StyleColor.optional(),
  background_color: StyleColor.optional(),
  error_correction: z.string().transform(s => s.trim().toUpperCase())
    .pipe(WorkCodeStyleEcSchema).optional(),
});
export type WorkCodeStyleOverride = z.infer<typeof WorkCodeStyleOverrideSchema>;

/** Что сервер РЕАЛЬНО сделал со стилем — в ответе манифеста, когда `style`
 *  был прислан. Параметр, который принимают и игнорируют, обязан либо
 *  работать, либо сказать об этом вслух. */
export const WorkCodeStyleEchoSchema = z.object({
  /** Применено к рендеру кодов; null = ни одного знакомого ключа. */
  applied: WorkCodeStyleOverrideSchema.nullable(),
  /** Ключи, которых сервер не знает — их рендер не видел. */
  ignored: z.array(z.string()),
});
export type WorkCodeStyleEcho = z.infer<typeof WorkCodeStyleEchoSchema>;

/** Ячейка (локаль × мерчант) — общий запрос плагинных GET'ов без параметров
 *  сверх адреса: `designReworks` и всё, что придёт следом. Манифест берёт его
 *  за базу и добавляет `style`; общая схема с необязательным `style` завела бы
 *  ручку, которая параметр объявляет, но в литерал parseReq не кладёт —
 *  ровно та дыра, из-за которой `.optional()` глотает поле молча. */
export const WorkDesignCellQuerySchema = z.object({
  locale: Locale,
  merchant: Merchant,
});
export type WorkDesignCellQuery = z.infer<typeof WorkDesignCellQuerySchema>;

export const WorkDesignManifestQuerySchema = WorkDesignCellQuerySchema.extend({
  /** url-encoded JSON `WorkCodeStyleOverride`. Разбирает `parseCodeStyleParam`
   *  (designflow/code-style.ts): битый JSON или неверное значение ЗНАКОМОГО
   *  ключа = 400 `invalid_style`, а не тихий откат на дефолтный стиль. */
  style: z.string().max(4000).optional(),
});
export type WorkDesignManifestQuery = z.infer<typeof WorkDesignManifestQuerySchema>;

export const WorkManifestCodeGeomSchema = z.object({
  /** Бокс svg кода в донорских координатах (bbox + поле маски). */
  box: z.tuple([z.number(), z.number(), z.number(), z.number()]),
  /** Бокс ГРУППЫ кода мастера (регион заплатки) — база: плагин кладёт svg
   *  долями box/region от фактического бокса слота на канвасе. */
  region: z.tuple([z.number(), z.number(), z.number(), z.number()]),
});
export type WorkManifestCodeGeom = z.infer<typeof WorkManifestCodeGeomSchema>;

export const WorkManifestCodeSchema = z.object({
  id: z.string(),
  value: z.string(),
  code_type: z.string(),
  svg_base64: z.string(),
  /** Есть только у кодов с заплаткой мастера (группа больше bbox). */
  geom: WorkManifestCodeGeomSchema.optional(),
});
export type WorkManifestCode = z.infer<typeof WorkManifestCodeSchema>;

export const WorkManifestStatusBarSchema = z.object({
  time: z.string(),
  battery: z.object({ state: z.string(), pct: z.number() }),
  network: z.object({
    id: z.string(),
    wifi_level: z.number(),
    signal_level: z.number(),
  }),
  focus_mode: z.string().optional(),
  location_arrow: z.boolean().optional(),
  rtl: z.boolean().optional(),
});
export type WorkManifestStatusBar = z.infer<typeof WorkManifestStatusBarSchema>;

export const WorkManifestParticipantSchema = z.object({
  participant_id: z.string(),
  display_id: z.string(),
  fields: z.record(z.string(), z.string()),
  codes: z.array(WorkManifestCodeSchema),
  status_bar: WorkManifestStatusBarSchema.optional(),
  /** Многомерчантный исходник: мерчант ЭТОГО кадра (иначе отсутствует). */
  merchant_name: z.string().optional(),
  /** Подбор данных (assemble.ts): картинки в image-поля мастера; байты —
   *  `GET /plugin/designDataAsset?id=`. */
  images: z.array(z.object({ key: z.string(), id: z.string() })).optional(),
});
export type WorkManifestParticipant = z.infer<typeof WorkManifestParticipantSchema>;

export const WorkDesignManifestResSchema = z.object({
  ok: z.boolean(),
  schema_version: z.literal(1),
  assignment: z.object({
    locale: z.string(),
    merchant_name: z.string(),
    participant_count: z.number(),
    generated_at: z.string(),
  }),
  participants: z.array(WorkManifestParticipantSchema),
  /** Сколько кадров рисовать (= participants.length). */
  n: z.number(),
  /** Занято по ячейке: принято заказчиком + уже сдано. */
  cap_used: z.number(),
  cap: z.number(),
  /** Версия мастера, к которой привязан этот пак. */
  version: z.number(),
  /** Кадры ТЕКУЩЕЙ версии, уже лежащие на сервере — плагин красит батчи. */
  sent_display_ids: z.array(z.string()),
  /** Есть, только если запрос нёс `?style=`: что применили, что не поняли. */
  code_style: WorkCodeStyleEchoSchema.optional(),
});
export type WorkDesignManifestRes = z.infer<typeof WorkDesignManifestResSchema>;


// ─── плагин: статус-бар, приём кадров, финализация ──────────────────────────

export const WorkStatusBarAssetsResSchema = z.object({
  ok: z.boolean(),
  assets: z.record(z.string(), z.string()),
  asset_count: z.number(),
});
export type WorkStatusBarAssetsRes = z.infer<typeof WorkStatusBarAssetsResSchema>;

/** multipart-поля `postScreens` (файл проверяется отдельно — он не JSON). */
export const WorkPostScreenFieldsSchema = z.object({
  locale: Locale,
  merchant: Merchant,
  participant_id: z.string().trim().min(1).max(200),
  display_id: z.string().trim().max(200).default(''),
});
export type WorkPostScreenFields = z.infer<typeof WorkPostScreenFieldsSchema>;

export const WorkPostScreenResSchema = z.object({
  ok: z.boolean(),
  r2_key: z.string(),
  bytes_size: z.number(),
  /** Версия, в которую лёг кадр: текущая версия мастера ЛИБО версия открытого
   *  реворка, если участника в текущем паке нет (перезалив старой версии,
   *  2026-09-02). */
  version: z.number(),
  /** true — кадр ушёл в сверку сразу (перезалив реворка старой версии:
   *  finalizeShipment ставит в очередь только текущую версию); false — ждёт
   *  finalizeShipment как обычно. */
  queued: z.boolean(),
});
export type WorkPostScreenRes = z.infer<typeof WorkPostScreenResSchema>;

/** 409 `postScreens`: участника кадра нет ни в паке текущей версии, ни среди
 *  открытых реворков ячейки (2026-09-02). 409, а не 400 — как у соседних
 *  состояний пака (`cell_closed`, `data_not_ready`, `manifest_codes_missing`):
 *  плагин по 409 глушит задание целиком и показывает `detail` одной строкой,
 *  а 400 роутер журналирует как server_error на КАЖДЫЙ кадр. */
export const WorkPostScreenUnknownParticipantResSchema = z.object({
  error: z.literal('unknown_participant'),
  /** Человеку: что пришло, где искали, что делать. Плагин показывает ЭТО. */
  detail: z.string(),
  participant_id: z.string(),
  display_id: z.string(),
  /** Текущая версия мастера ячейки — пак, в котором искали. */
  version: z.number(),
});
export type WorkPostScreenUnknownParticipantRes = z.infer<typeof WorkPostScreenUnknownParticipantResSchema>;

/** 409 `postScreens`, второй вид (2026-09-02): участник найден среди открытых
 *  реворков и в назначениях версии rejected-строки, но ожидаемых значений
 *  кодов по той версии у него нет (hi_IN/IndianOil v2 на проде: 20
 *  назначений, 19 строк кодов). Перезалив дал бы тот же `manifest_codes_missing`
 *  свипом через тик — код тот же, что у `finalizeShipment`, плагин глушит
 *  задание так же, как на `unknown_participant`. */
export const WorkPostScreenCodesMissingResSchema = WorkPostScreenUnknownParticipantResSchema.extend({
  error: z.literal('manifest_codes_missing'),
});
export type WorkPostScreenCodesMissingRes = z.infer<typeof WorkPostScreenCodesMissingResSchema>;

/** Любой 409 `postScreens` — состояние пака, не кадра: плагин по `error`
 *  останавливает задание и показывает `detail` одной строкой. */
export const WorkPostScreenConflictResSchema = z.discriminatedUnion('error', [
  WorkPostScreenUnknownParticipantResSchema,
  WorkPostScreenCodesMissingResSchema,
]);
export type WorkPostScreenConflictRes = z.infer<typeof WorkPostScreenConflictResSchema>;

/** Отклонённый QA кадр ячейки — превью реворка в плагине (2026-08-25):
 *  дизайнер по кнопке видит финальные PNG, которые вернули на перерисовку. */
export const WorkDesignReworkItemSchema = z.object({
  id: z.string(),
  display_id: z.string(),
  participant_id: z.string(),
  version: z.number(),
  sub_merchant: z.string().default(''),
  /** Причина отказа QA (из notes, без служебного префикса). */
  reason: z.string().default(''),
});
export type WorkDesignReworkItem = z.infer<typeof WorkDesignReworkItemSchema>;

export const WorkDesignReworksResSchema = z.object({
  ok: z.boolean(),
  items: z.array(WorkDesignReworkItemSchema).default([]),
});
export type WorkDesignReworksRes = z.infer<typeof WorkDesignReworksResSchema>;

// ─── Данные под манифест размножения (агенты, 2026-08-25) ───────────────────
// Кандидаты значений изменяемых полей, копятся с первого eval'а исходника;
// подбор выбирает из них после фиксации мастера (ready).
export const WorkDesignManifestDataItemSchema = z.object({
  /** Мерчант внутри агрегатора; '' — сама ячейка (единичный). */
  sub_merchant: z.string().trim().max(160).default(''),
  /** Ключ карты манифеста: offer_title, offer_desc, pic1… */
  field_key: z.string().trim().min(1).max(60),
  value: z.string().max(2000).default(''),
  /** Картинка-кандидат (логотип, карта), base64. */
  asset_b64: z.string().max(4_000_000).default(''),
  content_type: z.string().trim().max(60).default('image/png'),
  source: z.enum(['agent', 'research', 'eval', 'manual']).default('agent'),
  status: z.enum(['candidate', 'picked', 'rejected']).default('candidate'),
  note: z.string().max(300).default(''),
});
export type WorkDesignManifestDataItem = z.infer<typeof WorkDesignManifestDataItemSchema>;

export const WorkDesignManifestDataPutReqSchema = z.object({
  locale: Locale,
  merchant_name: Merchant,
  items: z.array(WorkDesignManifestDataItemSchema).min(1).max(50),
});
export type WorkDesignManifestDataPutReq = z.infer<typeof WorkDesignManifestDataPutReqSchema>;

export const WorkDesignManifestDataListQuerySchema = z.object({
  locale: Locale,
  merchant: Merchant,
  sub_merchant: z.string().trim().max(160).optional(),
  field_key: z.string().trim().max(60).optional(),
});
export type WorkDesignManifestDataListQuery = z.infer<typeof WorkDesignManifestDataListQuerySchema>;

export const WorkDesignManifestDataRowSchema = z.object({
  id: z.string(),
  locale: z.string(),
  merchant_name: z.string(),
  sub_merchant: z.string().default(''),
  field_key: z.string(),
  value: z.string().default(''),
  asset_r2_key: z.string().default(''),
  source: z.string().default(''),
  status: z.string().default(''),
  note: z.string().default(''),
  created_at: z.string().default(''),
});
export type WorkDesignManifestDataRow = z.infer<typeof WorkDesignManifestDataRowSchema>;

/** Тело `finishUploadJob` в ui.html: { locale, merchant_name }. */
export const WorkDesignFinalizeReqSchema = z.object({
  locale: Locale,
  merchant_name: Merchant,
});
export type WorkDesignFinalizeReq = z.infer<typeof WorkDesignFinalizeReqSchema>;

export const WorkDesignFrameErrorSchema = z.object({
  participant_id: z.string(),
  /** r2_missing | no_code_decoded | value_mismatch | symbology_mismatch | duplicate_code | manifest_codes_missing */
  kind: z.string(),
  detail: z.string().optional(),
  expected: z.string().optional(),
  decoded: z.array(z.string()).optional(),
  duplicate_with: z.string().optional(),
});
export type WorkDesignFrameError = z.infer<typeof WorkDesignFrameErrorSchema>;

// ── «Разметить для AI» (2026-08-26) ────────────────────────────────────────
// Пометки дизайнера поверх мастера (рамка + комментарий): боксы в
// координатах мастер-фрейма, сервер переводит в донорские пиксели,
// классифицирует комментарии и перекрывает бибоксы eval'а resolved-слоем.
export const WorkAiAnnotateReqSchema = z.object({
  locale: Locale,
  merchant_name: Merchant,
  /** Вариант исходника, на мастере которого стоят пометки ('' = основной). */
  variant_id: z.string().trim().max(60).default(''),
  frame_w: z.number().positive(),
  frame_h: z.number().positive(),
  notes: z.array(z.object({
    x: z.number(), y: z.number(),
    w: z.number().positive(), h: z.number().positive(),
    text: z.string().trim().min(1).max(300),
  })).min(1).max(40),
});
export type WorkAiAnnotateReq = z.infer<typeof WorkAiAnnotateReqSchema>;

export const WorkAiAnnotateResSchema = z.object({
  ok: z.boolean(),
  queued: z.number().optional(),
});
export type WorkAiAnnotateRes = z.infer<typeof WorkAiAnnotateResSchema>;

// ── Слой перевода мастера (2026-08-25) ─────────────────────────────────────
// POST /api/work/plugin/designTranslate: русский перевод всех OCR-строк
// донора; боксы — OCR-точные, плагин рисует по ним маски-подписи отдельным
// фреймом-сиблингом (в мастер и генерацию не попадает).
export const WorkDesignTranslateReqSchema = z.object({
  locale: Locale,
  merchant_name: Merchant,
});
export type WorkDesignTranslateReq = z.infer<typeof WorkDesignTranslateReqSchema>;

export const WorkDesignTranslateBlockSchema = z.object({
  bbox: z.object({ x: z.number(), y: z.number(), w: z.number(), h: z.number() }),
  original: z.string(),
  ru: z.string(),
});
export type WorkDesignTranslateBlock = z.infer<typeof WorkDesignTranslateBlockSchema>;

export const WorkDesignTranslateResSchema = z.object({
  ok: z.boolean(),
  image_w: z.number(),
  image_h: z.number(),
  cached: z.boolean().optional(),
  blocks: z.array(WorkDesignTranslateBlockSchema),
});
export type WorkDesignTranslateRes = z.infer<typeof WorkDesignTranslateResSchema>;

export const WorkDesignFinalizeResSchema = z.object({
  ok: z.boolean(),
  /** Сколько кадров поставлено в очередь фоновой сверки (async, 2026-08-25):
   *  декод и вердикты выносит крон sweepDesignerChecks, брак возвращается
   *  дизайнеру реворк-петлёй (status='rejected' + причина в notes). */
  queued: z.number().optional(),
  validated: z.number().optional(),
  idempotent: z.boolean().optional(),
  /** Сколько кадров ушло строками каталога в QA (indexbridge, 2026-08-25). */
  indexed: z.number().optional(),
  errors: z.array(WorkDesignFrameErrorSchema).optional(),
});
export type WorkDesignFinalizeRes = z.infer<typeof WorkDesignFinalizeResSchema>;
